"use client";

import Link from "next/link";
import { useState, useEffect, useCallback, useId, useRef, useSyncExternalStore } from "react";
import { getSupabaseAuthCookieName } from "@/lib/supabase/cookieName";
import { fetchWithTimeout } from "@/lib/fetchWithTimeout";
import { withTimeout } from "@/lib/withTimeout";
import { signOutLocalSession } from "@/lib/auth/logout";
import { getPostLoginRedirect } from "@/lib/auth/redirects";
import { clearAuthIntent, consumeAuthIntent } from "@/lib/auth/authIntent";
import { isBlockedAccountStatus, normalizeAccountStatus } from "@/lib/accountDeletion/status";
import {
  createBlockedLoginError,
  GENERIC_INVALID_CREDENTIALS_MESSAGE,
  isGenericInvalidCredentialsError,
  suppressAuthUiResetForCredentialsError,
} from "@/lib/auth/loginErrors";
import {
  getRequestedPathFromCurrentUrl,
} from "@/lib/auth/clientRedirectState";
import { recordPostLoginNavigation } from "@/lib/auth/postLoginNavigationDiagnostics";
import { buildOAuthCallbackUrl, logOAuthStart } from "@/lib/auth/oauthRedirect";
import {
  beginExplicitLoginIntent,
  clearExplicitLoginIntent,
  confirmExplicitPasswordLogin,
  isExplicitLoginCorrelationRequired,
} from "@/lib/auth/explicitLoginIntent";
import { resolveBusinessAccessContexts } from "@/lib/business/businessAccessContexts";
import {
  authErrorClassName,
  authForgotLinkClassName,
  authGoogleButtonClassName,
  authInputClassName,
  authLabelClassName,
  authPrimaryButtonClassName,
  authSwitchLinkClassName,
} from "@/components/auth/authFormStyles";

const MISSING_CREDENTIALS_MESSAGE = "Enter your email and password to sign in.";
const subscribeNoop = () => () => {};

function BusinessLoginInner({ isPopup, callbackError = "", sessionExpired = false }) {
  const authDiagEnabled = process.env.NEXT_PUBLIC_AUTH_DIAG === "1";
  const debugAuth = process.env.NEXT_PUBLIC_DEBUG_AUTH === "1";
  const authTimeoutMs = 30000;
  const profileTimeoutMs = 15000;
  const refreshTimeoutMs = 15000;
  const overallTimeoutMs = 45000;
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL?.replace(/\/+$/, "");

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const authErrorId = useId();
  // False in the server HTML and during hydration, true afterwards. Callback errors wait for it so
  // their role="alert" region is inserted on the client, where screen readers announce it.
  const hydrated = useSyncExternalStore(subscribeNoop, () => true, () => false);
  const displayedAuthError =
    authError ||
    (!hydrated
      ? ""
      : callbackError === "invalid_credentials"
        ? GENERIC_INVALID_CREDENTIALS_MESSAGE
        : callbackError === "customer_account_exists"
          ? "This Google account is already registered as a customer. Use a different email for your business account or contact support."
          : "");
  const redirectingRef = useRef(false);
  const mountedRef = useRef(false);
  const pendingRef = useRef(false);
  const attemptRef = useRef(0);
  const didCompleteRef = useRef(false);
  const timeoutIdRef = useRef(null);
  const timeoutControllerRef = useRef(null);
  const flowIdRef = useRef(
    `auth-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  );
  const sessionRef = useRef(null);
  const supabaseRef = useRef(null);

  const getSupabase = useCallback(async () => {
    if (!supabaseRef.current) {
      const { getSupabaseBrowserClient } = await import("@/lib/supabase/browser");
      supabaseRef.current = getSupabaseBrowserClient();
    }
    return supabaseRef.current;
  }, []);

  const authDiagLog = useCallback(
    (event, payload = {}) => {
      if (!authDiagEnabled || typeof window === "undefined") return;
      const timestamp = new Date().toISOString();
      console.log("[AUTH_DIAG]", {
        timestamp,
        pathname: window.location.pathname,
        queryKeys: Array.from(new URLSearchParams(window.location.search).keys()).sort(),
        flowId: flowIdRef.current,
        event,
        ...payload,
      });
    },
    [authDiagEnabled]
  );

  const logAuthRequest = useCallback(
    (payload) => {
      if (!authDiagEnabled && !debugAuth) return;
      console.log("[AUTH_DEBUG]", payload);
    },
    [authDiagEnabled, debugAuth]
  );

  const isTimeoutError = useCallback((err) => {
    const name = err?.name || "";
    const message = String(err?.message || "").toLowerCase();
    return (
      name === "TimeoutError" ||
      name === "AbortError" ||
      message.includes("timed out")
    );
  }, []);

  const closeAuthAttempt = useCallback(
    (attemptId, reason) => {
      authDiagLog("auth:attempt:end", {
        action: "business_login",
        attemptId,
        reason,
      });
    },
    [authDiagLog]
  );

  const getCookieStatus = useCallback(() => {
    if (typeof document === "undefined") return null;
    const cookieName = getSupabaseAuthCookieName();
    const cookieLength = document.cookie.length;
    const names = document.cookie
      .split(";")
      .map((entry) => entry.trim().split("=")[0])
      .filter(Boolean);
    const hasAuthCookie = cookieName
      ? names.some(
          (name) => name === cookieName || name.startsWith(`${cookieName}.`)
        )
      : false;
    return {
      cookieName,
      cookieLength,
      hasAuthCookie,
    };
  }, []);

  const waitForAuthCookie = useCallback(async (timeoutMs = 2500) => {
    if (typeof document === "undefined") return false;
    const cookieName = getSupabaseAuthCookieName();
    if (!cookieName) return false;

    const hasAuthCookie = () => {
      const names = document.cookie
        .split(";")
        .map((entry) => entry.trim().split("=")[0])
        .filter(Boolean);
      return names.some(
        (name) => name === cookieName || name.startsWith(`${cookieName}.`)
      );
    };

    if (hasAuthCookie()) return true;

    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      if (hasAuthCookie()) return true;
    }

    return false;
  }, []);

  const resolvePostLoginTarget = useCallback(() => {
    const requestedPath = getRequestedPathFromCurrentUrl();
    const intentPath = consumeAuthIntent({
      role: "business",
      fallbackPath: requestedPath || "/business/dashboard",
    });
    const target = getPostLoginRedirect({
      role: "business",
      requestedPath: intentPath || requestedPath,
      fallbackPath: "/business/dashboard",
    });
    return target;
  }, []);

  const finishBusinessAuth = useCallback(
    (target = "/business/dashboard") => {
      if (redirectingRef.current) return;
      redirectingRef.current = true;
      authDiagLog("redirect:start", { isPopup });

      if (typeof window !== "undefined") {
        try {
          // Broadcast success so other tabs (the opener) can react
          localStorage.setItem("business_auth_redirect", target);
          localStorage.setItem("business_auth_success", Date.now().toString());

          if (typeof BroadcastChannel !== "undefined") {
            const channel = new BroadcastChannel("yb-business-auth");
            channel.postMessage({ type: "YB_BUSINESS_AUTH_SUCCESS", target });
            channel.close();
          }
        } catch (err) {
          console.warn("Could not broadcast business auth success", err);
        }

        if (isPopup) {
          // Close popup when possible; fall back to in-tab redirect if blocked
          if (window.opener && window.location.origin) {
            try {
              authDiagLog("popup:postMessage");
              window.opener.postMessage(
                { type: "YB_BUSINESS_AUTH_SUCCESS", target },
                window.location.origin
              );
            } catch (err) {
              console.warn("Popup postMessage failed", err);
            }
          }
          setTimeout(() => {
            window.close();

            // Some browsers ignore close() if not opened by script
            setTimeout(() => {
              if (!window.closed) {
                authDiagLog("popup:close:blocked");
                authDiagLog("redirect:location_replace");
                recordPostLoginNavigation({
                  initiatedBy: "window.location.replace",
                  target,
                });
                window.location.replace(target);
              }
            }, 250);
          }, 250);

          return;
        }
      }

      authDiagLog("redirect:location_replace");
      recordPostLoginNavigation({
        initiatedBy: "window.location.replace",
        target,
      });
      window.location.replace(target);
    },
    [authDiagLog, isPopup]
  );

  const redirectToDashboard = useCallback(async () => {
    if (redirectingRef.current) return;
    await waitForAuthCookie();
    finishBusinessAuth(resolvePostLoginTarget());
  }, [finishBusinessAuth, resolvePostLoginTarget, waitForAuthCookie]);

  const handleLoginTimeout = useCallback(
    async (attemptId, timeoutController) => {
      if (didCompleteRef.current || attemptRef.current !== attemptId) return;
      timeoutController.abort(new Error("timeout"));
      pendingRef.current = false;

      let activeSession = sessionRef.current;
      try {
        const client = supabaseRef.current ?? (await getSupabase());
        const { data } = await client.auth.getSession();
        if (data?.session) {
          activeSession = data.session;
          sessionRef.current = data.session;
        }
      } catch (err) {
        console.warn("Could not read session after timeout", err);
      }

      authDiagLog("login:timeout", {
        attemptId,
        hasSession: Boolean(activeSession),
      });

      if (activeSession) {
        didCompleteRef.current = true;
        if (mountedRef.current) {
          setLoading(false);
        }
        closeAuthAttempt(attemptId, "session");
        await redirectToDashboard();
        return;
      }

      if (mountedRef.current) {
        setLoading(false);
        setAuthError(
          `Login request timed out after ${Math.round(overallTimeoutMs / 1000)}s. Please check your connection and try again.`
        );
      }
      closeAuthAttempt(attemptId, "timeout");
    },
    [authDiagLog, closeAuthAttempt, overallTimeoutMs, redirectToDashboard, getSupabase]
  );

  async function handleLogin(e) {
    e.preventDefault();
    if (pendingRef.current || loading) return;
    const normalizedEmail = String(email || "").trim();
    const hasEmail = Boolean(normalizedEmail);
    const hasPassword = Boolean(password);
    if (process.env.NODE_ENV !== "production" || authDiagEnabled || debugAuth) {
      console.info("[AUTH_PASSWORD_BOUNDARY]", {
        component: "BusinessLoginClient",
        event: "submit",
        pathname: typeof window !== "undefined" ? window.location.pathname : null,
        search: typeof window !== "undefined" ? window.location.search : null,
        activeAuthMode: "business_password",
        submitEventFired: true,
        emailPresent: hasEmail,
        passwordPresent: hasPassword,
      });
    }
    if (!hasEmail || !hasPassword) {
      setAuthError(MISSING_CREDENTIALS_MESSAGE);
      return;
    }
    let supabase = null;
    try {
      supabase = await getSupabase();
    } catch {
      setAuthError("Auth client not ready. Please refresh and try again.");
      return;
    }
    const attemptId = attemptRef.current + 1;
    attemptRef.current = attemptId;
    authDiagLog("auth:attempt:begin", {
      action: "business_login",
      attemptId,
    });
    didCompleteRef.current = false;
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    if (timeoutControllerRef.current) {
      timeoutControllerRef.current.abort();
    }

    const timeoutController = new AbortController();
    timeoutControllerRef.current = timeoutController;
    timeoutIdRef.current = setTimeout(() => {
      void handleLoginTimeout(attemptId, timeoutController);
    }, overallTimeoutMs);

    pendingRef.current = true;
    if (mountedRef.current) {
      setLoading(true);
      setAuthError("");
    }
    authDiagLog("login:submit:start", { attemptId, isPopup });

    try {
      try {
        localStorage.setItem("signup_role", "business");
      } catch (err) {
        console.warn("Could not set signup role", err);
      }

      const signInStart = typeof performance !== "undefined" ? performance.now() : Date.now();
      if (process.env.NODE_ENV !== "production" || authDiagEnabled || debugAuth) {
        console.info("[AUTH_PASSWORD_BOUNDARY]", {
          component: "BusinessLoginClient",
          event: "signInWithPassword:start",
          pathname: typeof window !== "undefined" ? window.location.pathname : null,
          search: typeof window !== "undefined" ? window.location.search : null,
          activeAuthMode: "business_password",
          submitEventFired: true,
          emailPresent: hasEmail,
          passwordPresent: hasPassword,
        });
      }
      const explicitLoginIntent = beginExplicitLoginIntent("password");
      const signInResult = await withTimeout(
        supabase.auth.signInWithPassword({ email: normalizedEmail, password }),
        authTimeoutMs,
        `Login request timed out after ${Math.round(authTimeoutMs / 1000)}s`
      );
      const signInDurationMs = Math.round(
        (typeof performance !== "undefined" ? performance.now() : Date.now()) -
          signInStart
      );
      logAuthRequest({
        label: "supabase.signInWithPassword",
        url: supabaseUrl ? `${supabaseUrl}/auth/v1/token` : null,
        method: "POST",
        timeoutMs: authTimeoutMs,
        durationMs: signInDurationMs,
        status: signInResult?.error?.status ?? null,
        error: signInResult?.error?.message ?? null,
      });
      const { data, error } = signInResult;
      authDiagLog("login:signIn:result", {
        attemptId,
        hasSession: Boolean(data?.session),
        error: error?.message ?? null,
      });

      if (timeoutController.signal.aborted || attemptRef.current !== attemptId) {
        return;
      }

      if (error) {
        clearExplicitLoginIntent(explicitLoginIntent?.id);
        throw error;
      }
      if (
        !confirmExplicitPasswordLogin(explicitLoginIntent, data?.session) &&
        isExplicitLoginCorrelationRequired()
      ) {
        throw new Error("We couldn't confirm this sign-in. Please try again.");
      }

      const user = data.user;
      sessionRef.current = data?.session ?? null;

      const profileQuery = supabase
        .from("users")
        .select("role,account_status")
        .eq("id", user.id)
        .maybeSingle();
      const profileStart = typeof performance !== "undefined" ? performance.now() : Date.now();
      const profileResult = await withTimeout(
        typeof profileQuery.abortSignal === "function"
          ? profileQuery.abortSignal(timeoutController.signal)
          : profileQuery,
        profileTimeoutMs,
        `Profile request timed out after ${Math.round(profileTimeoutMs / 1000)}s`
      );
      const profileDurationMs = Math.round(
        (typeof performance !== "undefined" ? performance.now() : Date.now()) -
          profileStart
      );
      logAuthRequest({
        label: "supabase.profile.role",
        url: supabaseUrl ? `${supabaseUrl}/rest/v1/users` : null,
        method: "GET",
        timeoutMs: profileTimeoutMs,
        durationMs: profileDurationMs,
        status: profileResult?.error?.status ?? null,
        error: profileResult?.error?.message ?? null,
      });
      const { data: profile, error: profileErr } = profileResult;

      if (timeoutController.signal.aborted || attemptRef.current !== attemptId) {
        return;
      }

      if (profileErr) {
        throw profileErr;
      }
      const accountStatus = normalizeAccountStatus(profile?.account_status);
      if (isBlockedAccountStatus(accountStatus)) {
        suppressAuthUiResetForCredentialsError();
        await signOutLocalSession(supabase, "local");
        throw createBlockedLoginError();
      }

      const businessAccess = await resolveBusinessAccessContexts(supabase, user.id);
      if (businessAccess.error) {
        throw businessAccess.error;
      }
      const hasContextualBusinessAccess = businessAccess.hasBusinessAccess === true;
      if (profile?.role !== "business" && !hasContextualBusinessAccess) {
        await signOutLocalSession(supabase, "local");
        throw new Error("Only business accounts can log in here.");
      }

      const session = sessionRef.current;
      if (session?.access_token && session?.refresh_token) {
        const refreshStart = typeof performance !== "undefined" ? performance.now() : Date.now();
        const response = await fetchWithTimeout("/api/auth/refresh", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              access_token: session.access_token,
              refresh_token: session.refresh_token,
            }),
            timeoutMs: refreshTimeoutMs,
            signal: timeoutController.signal,
          });
        const refreshDurationMs = Math.round(
          (typeof performance !== "undefined" ? performance.now() : Date.now()) -
            refreshStart
        );
        logAuthRequest({
          label: "next.auth.refresh",
          url: "/api/auth/refresh",
          method: "POST",
          timeoutMs: refreshTimeoutMs,
          durationMs: refreshDurationMs,
          status: response?.status ?? null,
          error: response?.ok ? null : `HTTP ${response.status}`,
        });

        if (timeoutController.signal.aborted || attemptRef.current !== attemptId) {
          return;
        }

        if (!response.ok) {
          throw new Error("Session refresh failed. Please try again.");
        }
        if (response.headers.get("x-auth-refresh-user") !== "1") {
          throw new Error("We couldn't finish signing you in. Please try again.");
        }
      }

      didCompleteRef.current = true;
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      await redirectToDashboard();
      authDiagLog("login:submit:success", { attemptId });
    } catch (err) {
      if (!didCompleteRef.current) clearExplicitLoginIntent();
      if (
        timeoutController.signal.aborted ||
        attemptRef.current !== attemptId
      ) {
        authDiagLog("login:submit:aborted", { attemptId });
        return;
      }
      if (!didCompleteRef.current && attemptRef.current === attemptId) {
        if (!isGenericInvalidCredentialsError(err) && !isTimeoutError(err)) {
          console.error("Business login failed", err);
        }
        const message = isTimeoutError(err)
          ? "Login request timed out. Please check your connection and try again."
          : isGenericInvalidCredentialsError(err)
            ? GENERIC_INVALID_CREDENTIALS_MESSAGE
            : err?.message ?? "Login failed. Please refresh and try again.";
        authDiagLog("login:submit:error", { attemptId, message });
        if (mountedRef.current) {
          setAuthError(message);
        }
      }
    } finally {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      if (attemptRef.current === attemptId) {
        pendingRef.current = false;
        if (mountedRef.current) {
          setLoading(false);
        }
      }
      closeAuthAttempt(attemptId, "password");
      authDiagLog("login:submit:end", { attemptId });
    }
  }

  async function handleGoogleLogin() {
    if (pendingRef.current || loading) return;
    let supabase = null;
    try {
      supabase = await getSupabase();
    } catch {
      setAuthError("Auth client not ready. Please refresh and try again.");
      return;
    }
    let attemptId = 0;
    try {
      attemptId = attemptRef.current + 1;
      attemptRef.current = attemptId;
      authDiagLog("auth:attempt:begin", {
        action: "business_oauth",
        attemptId,
      });
      pendingRef.current = true;
      if (mountedRef.current) {
        setLoading(true);
        setAuthError("");
      }

      const currentOrigin =
        typeof window !== "undefined" ? window.location.origin : "http://localhost:3000";
      const googleTarget = getPostLoginRedirect({
        role: "business",
        requestedPath: getRequestedPathFromCurrentUrl(),
        fallbackPath: "/business/onboarding",
      });
      const explicitLoginIntent = beginExplicitLoginIntent("oauth");
      const redirectTo = buildOAuthCallbackUrl({
        currentOrigin,
        next: googleTarget,
        intent: "business",
        loginIntentId: explicitLoginIntent?.id,
      });
      logOAuthStart({ provider: "google", redirectTo, currentOrigin });

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo },
      });

      if (error) {
        clearExplicitLoginIntent();
        console.error("Google login error:", error);
        clearAuthIntent();
        if (mountedRef.current) {
          setAuthError("Failed to sign in with Google.");
          setLoading(false);
        }
        pendingRef.current = false;
        return;
      }
    } finally {
      if (attemptId) {
        authDiagLog("auth:attempt:end", {
          action: "business_oauth",
          attemptId,
        });
      }
    }
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      if (timeoutControllerRef.current) {
        timeoutControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (!authDiagEnabled) return;

    try {
      sessionStorage.setItem("auth_flow_id", flowIdRef.current);
    } catch (err) {
      console.warn("Could not persist auth flow id", err);
    }

    authDiagLog("login:mount", { popup: isPopup });
    const cookieStatus = getCookieStatus();
    authDiagLog("login:cookies", cookieStatus ?? {});

    const handleError = (event) => {
      authDiagLog("login:window:error", {
        message: event?.message ?? null,
        filename: event?.filename ?? null,
        lineno: event?.lineno ?? null,
        colno: event?.colno ?? null,
      });
    };

    const handleRejection = (event) => {
      const reason = event?.reason;
      authDiagLog("login:window:unhandledrejection", {
        reason: reason?.message ?? String(reason ?? ""),
      });
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, [authDiagEnabled, authDiagLog, getCookieStatus, isPopup]);

  // Only the missing-credentials error belongs to specific fields; credential failures stay form-level.
  const missingCredentials = displayedAuthError === MISSING_CREDENTIALS_MESSAGE;
  const emailInvalid = missingCredentials && !String(email || "").trim();
  const passwordInvalid = missingCredentials && !password;

  return (
    <div
      className="max-w-md w-full p-8 rounded-2xl border border-[var(--yb-border)] bg-white animate-fadeIn"
    >
        <h1 className="text-3xl font-extrabold text-center mb-3 tracking-tight text-slate-900">
          Business Login
        </h1>

          <p className="text-center mb-6 text-slate-600">
            Sign in to manage your business
          </p>

          {callbackError === "magic_link_expired" ? (
            <div className="mb-4 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              This sign-in link has expired. Please request a new magic link below.
            </div>
          ) : null}
          {callbackError === "auth_callback_failed" ? (
            <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              We couldn&apos;t complete sign-in from that link. Please try again.
            </div>
          ) : null}
          {sessionExpired ? (
            <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              Your session expired. Please log in again.
            </div>
          ) : null}

          <form onSubmit={handleLogin}>
            <div className="mb-4 space-y-2">
              <label
                htmlFor="business-login-email"
                className={authLabelClassName}
              >
                Email
              </label>
              <input
                id="business-login-email"
                name="email"
                type="email"
                className={authInputClassName}
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={emailInvalid || undefined}
                aria-describedby={emailInvalid ? authErrorId : undefined}
                required
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="business-login-password"
                className={authLabelClassName}
              >
                Password
              </label>
              <input
                id="business-login-password"
                name="password"
                type="password"
                className={authInputClassName}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={passwordInvalid || undefined}
                aria-describedby={passwordInvalid ? authErrorId : undefined}
                required
              />
              <div className="mt-3 pb-1 text-right">
                <Link
                  href="/auth/forgot-password"
                  className={authForgotLinkClassName}
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <div className="pt-3">
              {displayedAuthError ? (
                <div id={authErrorId} role="alert" className={authErrorClassName}>
                  {displayedAuthError}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={loading}
                className={`${authPrimaryButtonClassName} yb-auth-cta`}
              >
                {loading ? "Signing in..." : "Log in"}
              </button>
            </div>
          </form>

          <button
            type="button"
            onClick={handleGoogleLogin}
            className={`${authGoogleButtonClassName} mt-5`}
          >
            <img src="/google-icon.svg" className="h-5 w-5" alt="Google" />
            Continue with Google
          </button>

          <p className="text-center text-sm mt-4 text-slate-600">
            Don&apos;t have an account?{" "}
            <a
              href="/business-auth/register"
              className={authSwitchLinkClassName}
            >
              Sign up
            </a>
          </p>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out;
        }
      `}</style>
    </div>
  );
}

export default function BusinessLoginClient({
  isPopup = false,
  callbackError = "",
  sessionExpired = false,
}) {
  return (
    <BusinessLoginInner
      isPopup={isPopup}
      callbackError={callbackError}
      sessionExpired={sessionExpired}
    />
  );
}
